<!--
  InviteClientModal — Distributor-side modal for sending a Store Owner
  invitation to a Client contact (ADR 0050 / Phase F5).

  Two modes, selected by the ``clientId`` prop:

    * INVITE-EXISTING (``clientId`` is a real id): collects the recipient
      email + optional name fields and POSTs to
      ``/api/clients/<id>/invitations``. Used by ClientList row actions
      and the workspace detail page's Resend.

    * CREATE-NEW (``clientId`` is falsy -- 0 / null): the "Invite a
      Client" affordance for a brand-new Client. Collects a required
      Client company in addition to the contact email, CREATES the Client
      first (POST /api/clients/ via the clients store -- tenant-scoped to
      the caller's effective distributor, impersonation-aware), THEN
      sends the invitation against the newly-minted id.

      This mode fixes the regression where "Invite a Client" silently
      re-invited the FIRST existing client (clients[0]) instead of
      creating the new one the user typed -- so no new Client row ever
      persisted (most visible while a Super Admin was impersonating a
      distributor).

  On success, emits ``sent`` (with the created/invited client payload in
  create mode) so the parent can refresh; on failure, surfaces the error
  inline and stays open.

  Copy is matter-of-fact and customer-facing — the recipient is a
  real Client contact, not an internal QA persona. Do NOT introduce
  demo / sandbox terminology here.

  Capability gate: the parent (ClientList / ClientsListPage) hides the
  trigger when the caller does not hold ``client.invite_admin``. The
  modal itself does not re-check; the backend is the authority.
-->
<template>
  <div
    v-if="open"
    class="invite-modal-backdrop"
    data-testid="invite-client-modal"
    @click.self="cancel"
  >
    <div class="invite-modal" role="dialog" aria-modal="true">
      <h3 class="invite-modal__title">Invite a Client</h3>
      <p class="invite-modal__subtext">
        The Client will receive an email with a link to set up their
        account and start managing their store.
      </p>

      <form @submit.prevent="submit">
        <label
          v-if="isCreateMode"
          class="invite-modal__label"
          for="invite-client-name"
        >
          Client company
          <input
            id="invite-client-name"
            v-model="clientNameInput"
            type="text"
            class="invite-modal__input"
            data-testid="invite-modal-client-name"
            placeholder="Acme Apparel"
            autocomplete="off"
            required
          />
        </label>

        <label class="invite-modal__label" for="invite-email">
          Email
          <input
            id="invite-email"
            v-model="email"
            type="email"
            class="invite-modal__input"
            data-testid="invite-modal-email"
            placeholder="contact@example.com"
            autocomplete="off"
            required
          />
        </label>

        <div class="invite-modal__row">
          <label class="invite-modal__label" for="invite-first">
            First name (optional)
            <input
              id="invite-first"
              v-model="firstName"
              type="text"
              class="invite-modal__input"
              data-testid="invite-modal-first-name"
              autocomplete="off"
            />
          </label>
          <label class="invite-modal__label" for="invite-last">
            Last name (optional)
            <input
              id="invite-last"
              v-model="lastName"
              type="text"
              class="invite-modal__input"
              data-testid="invite-modal-last-name"
              autocomplete="off"
            />
          </label>
        </div>

        <p
          v-if="warningMessage"
          class="invite-modal__warning"
          data-testid="invite-modal-warning"
        >
          {{ warningMessage }}
        </p>

        <p
          v-if="errorMessage"
          class="invite-modal__error"
          data-testid="invite-modal-error"
        >
          {{ errorMessage }}
        </p>

        <div class="invite-modal__actions">
          <button
            type="button"
            class="invite-modal__btn"
            data-testid="invite-modal-cancel"
            :disabled="submitting"
            @click="cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="invite-modal__btn invite-modal__btn--primary"
            data-testid="invite-modal-send"
            :disabled="!canSubmit || submitting"
          >
            {{ submitting ? 'Sending...' : 'Send invitation' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { useClientsStore } from '@/stores/clients'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'InviteClientModal',
  props: {
    open: { type: Boolean, default: false },
    // Real client id for INVITE-EXISTING mode (the F5 per-client
    // invite/resend). Ignored in create-new mode. Defaults to 0 so the
    // create-new callers don't have to bind a placeholder; the active
    // mode is selected EXPLICITLY by ``createNew`` below, never inferred
    // from a falsy id (which would silently change behavior for any
    // caller that happens to pass 0).
    clientId: { type: [Number, String], default: 0 },
    clientName: { type: String, default: '' },
    // CREATE-NEW mode: the modal creates a brand-new Client (POST
    // /clients/, tenant-scoped/impersonation-aware) BEFORE sending the
    // invitation against the new id. Opt-in per caller -- the F5
    // invite-existing surfaces (ClientList row, StoreClientContact) leave
    // this false and pass a real ``clientId``.
    createNew: { type: Boolean, default: false },
  },
  emits: ['close', 'sent'],
  data() {
    return {
      clientNameInput: '',
      email: '',
      firstName: '',
      lastName: '',
      submitting: false,
      errorMessage: '',
      warningMessage: '',
    }
  },
  computed: {
    clientsStore() {
      return useClientsStore()
    },
    // CREATE-NEW is an EXPLICIT opt-in via the ``createNew`` prop -- it is
    // NOT inferred from a falsy ``clientId``. Inferring it from the id
    // would silently flip behavior for any caller that passes 0/null.
    isCreateMode() {
      return this.createNew === true
    },
    canSubmit() {
      if (!EMAIL_RE.test(this.email.trim())) return false
      // Create mode additionally requires a Client name to scope the new
      // Client row to (clients.name is NOT NULL).
      if (this.isCreateMode && !this.clientNameInput.trim()) return false
      return true
    },
  },
  watch: {
    open(v) {
      if (v) {
        // Reset state when the modal re-opens so a prior send's data
        // doesn't bleed into the next invitation.
        this.clientNameInput = ''
        this.email = ''
        this.firstName = ''
        this.lastName = ''
        this.errorMessage = ''
        this.warningMessage = ''
      }
    },
  },
  methods: {
    cancel() {
      if (this.submitting) return
      this.$emit('close')
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.errorMessage = ''
      try {
        // In create mode, mint the Client FIRST so the invitation has a
        // real id to attach to. If creation fails we surface the error
        // and do NOT send an invitation (no orphaned invite, no
        // wrong-client send).
        let targetClientId = this.clientId
        if (this.isCreateMode) {
          const created = await this.clientsStore.createClient({
            name: this.clientNameInput.trim(),
            email: this.email.trim().toLowerCase(),
            status: 'active',
          })
          if (!created || created.id == null) {
            throw new Error('Client creation returned no id')
          }
          targetClientId = created.id
        }

        const payload = {
          email: this.email.trim().toLowerCase(),
        }
        if (this.firstName.trim()) payload.first_name = this.firstName.trim()
        if (this.lastName.trim()) payload.last_name = this.lastName.trim()

        const resp = await api.post(
          `/clients/${targetClientId}/invitations`,
          payload,
        )
        // The invitation row is always persisted on 201 -- emit sent so
        // the parent can refresh the client list regardless.
        this.$emit('sent', { ...resp.data, client_id: targetClientId })
        if (resp.data && resp.data.warning) {
          // Email delivery failed but the invitation row was saved.
          // Surface the warning inline and keep the modal open so the
          // user is not left wondering why no email arrived.
          this.warningMessage =
            'Invitation saved, but the email could not be sent. ' +
            'Please try again or contact support.'
        } else {
          this.$emit('close')
        }
      } catch (err) {
        const status = err?.response?.status
        const detail =
          err?.response?.data?.message ||
          err?.response?.data?.description ||
          (Array.isArray(err?.response?.data?.errors) &&
            err.response.data.errors[0] &&
            (err.response.data.errors[0].detail ||
              err.response.data.errors[0].title)) ||
          err?.message
        if (status === 403) {
          this.errorMessage =
            "You don't have permission to send invitations for this Client."
        } else if (status === 400) {
          this.errorMessage =
            detail || 'Check the details and try again.'
        } else {
          this.errorMessage = detail || 'Could not send the invitation.'
        }
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.invite-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.invite-modal {
  background: var(--tf-bg, #fff);
  padding: 24px;
  border-radius: var(--tf-radius-sm, 6px);
  max-width: 480px;
  width: 90%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}
.invite-modal__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--tf-fg-1);
}
.invite-modal__subtext {
  margin: 0 0 16px;
  color: var(--tf-fg-2);
  font-size: 14px;
}
.invite-modal__label {
  display: block;
  font-size: 13px;
  color: var(--tf-fg-2);
  margin-bottom: 12px;
  flex: 1;
}
.invite-modal__input {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--tf-border, #d0d4dc);
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 14px;
  margin-top: 4px;
  background: var(--tf-bg, #fff);
  color: var(--tf-fg-1, #111);
}
.invite-modal__row {
  display: flex;
  gap: 12px;
}
.invite-modal__warning {
  color: #7c5d00;
  background: #fff8e1;
  border: 1px solid #f9c840;
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 13px;
  padding: 8px 10px;
  margin: 0 0 12px;
}
.invite-modal__error {
  color: #c0392b;
  font-size: 13px;
  margin: 0 0 12px;
}
.invite-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.invite-modal__btn {
  padding: 8px 16px;
  border: 1px solid var(--tf-border-strong, #98a0ad);
  background: var(--tf-bg, #fff);
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 14px;
  cursor: pointer;
}
.invite-modal__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.invite-modal__btn--primary {
  background: var(--tf-accent, #2c5fff);
  color: #fff;
  border-color: var(--tf-accent, #2c5fff);
}
.invite-modal__btn--primary:hover:not(:disabled) {
  filter: brightness(0.95);
}
</style>
